import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, Plus, Trash2, MapPin, Package, Wand2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { REQUIRED_BIBLE_FIELDS, getStoryManifest, type SeriesBible, type SeriesLocation, type SeriesProp, type SeriesCharacter } from '../../services/studioService';
import AgentSeat, { type SeatCandidate } from './AgentSeat';
import AiAssist from './AiAssist';
import ReferencePlate from './ReferencePlate';
import ProjectCast from './ProjectCast';
import ProjectEpisodes from './ProjectEpisodes';
import TemplateModal, { type StoryTemplate } from './TemplateModal';
import { Lightbulb } from 'lucide-react';
import AutoTextarea from '../ui/AutoTextarea';

/**
 * THE PROJECT PAGE — the micro-drama's own facts, above any one episode.
 *
 * Owner 2026-08-24: *"we need high-level plot, characters, world settings (time, where, etc) at project level;
 * then at episode level, the script and with which characters, and where, inheriting from project-level."*
 *
 * The two levels had been collapsed into one page. "What is your film about?" was asked once per EPISODE, so
 * the world was re-invented on every draft — which is how a hacker premise came back as a CEO romance
 * (BUGS.md 2026-08-23): with nothing durable to write against, the genre's own world was the most concrete
 * thing in the prompt. State it once here and every episode inherits it.
 *
 * WORLD IS TWO FIELDS, not a paragraph. One box asking for plot AND era AND place reliably returns the plot.
 *
 * Nothing is hard-gated (the Studio never traps a creator mid-journey), but the page says plainly what is
 * still outstanding, because an episode written against a blank project is the failure this page exists to
 * prevent. Presentational: the draft is local, persistence is the caller's.
 */

/** Only the PROSE fields are laid out by the loop below; `locations` is a list and has its own section. */
type ProseKey = Exclude<keyof SeriesBible, 'locations' | 'props' | 'cast' | 'plan' | 'episode_target'>;

interface FieldDef {
  key: ProseKey;
  label: string;
  hint: string;
  rows: number;
  placeholder: string;
  /** No Write it for me. Only for a field an AI cannot honestly fill (see `established`). */
  noAssist?: boolean;
}

const FIELDS: FieldDef[] = [
  {
    key: 'premise', label: 'The plot', rows: 3,
    hint: 'What the whole thing is about — the situation every episode plays out inside.',
    placeholder: 'A courier discovers the system that assigns everyone their work has been rewriting her life for years.',
  },
  {
    key: 'era', label: 'When', rows: 1,
    hint: 'The year, period or era.',
    placeholder: '2077, thirty years after the grid went private',
  },
  {
    key: 'setting', label: 'Where', rows: 2,
    hint: 'The place it lives in, and how it feels to be there.',
    placeholder: 'A flooded Seoul: server towers above the waterline, markets on the walkways between them.',
  },
  /**
   * HOW IT LOOKS — stated once, used by every picture the production makes (F1, owner 2026-09-12).
   *
   * It sits directly after WHEN and WHERE because it is the third fact about the world, and before the story
   * fields because it governs them visually. Every reference plate — a set photograph, an object plate, a
   * character portrait — used to be generated with no idea what the show looks like, so one project could
   * produce a photoreal room, a catalogue knife and an anime face.
   *
   * NOT in REQUIRED: a project written before this existed must not suddenly read as incomplete, and a
   * creator with no style opinion should still be able to get an episode out.
   */
  {
    key: 'look', label: 'How it looks', rows: 1,
    hint: 'The style every shot is made in. A medium, a camera feel, a palette — kept to a line, because a paragraph of style makes the picture drift.',
    placeholder: 'Photoreal, handheld 35mm, cool teal light',
  },
  {
    key: 'arc', label: 'Where it is heading', rows: 2,
    hint: 'Optional. The overall story, so each episode ladders toward something.',
    placeholder: 'She stops running the routes and starts writing them.',
  },
  {
    key: 'established', label: 'Already established', rows: 3,
    hint: 'Optional. Facts later episodes must not contradict — deaths, reveals, promises made.',
    placeholder: '',
    /* CANON IS RECORDED, NEVER INVENTED (BUGS.md 2026-09-27). This field is what episodes have already made
       true, so the creator writes it; an AI filling it for a new series would be making canon up. The server
       never offered it either, so the button only ever failed. */
    noAssist: true,
  },
];

const REQUIRED = new Set<ProseKey>(REQUIRED_BIBLE_FIELDS as ProseKey[]);

/** Mirrors the server's own ceiling (`seriesRepository.MAX_LOCATIONS`) so the button disables before a save is
 *  silently trimmed — a limit the client does not know about is a limit the creator discovers by losing work. */
const MAX_LOCATIONS = 6;

export interface ProjectStageProps {
  /** The project's name. Renamed here or in the explorer — the same fact, either way. */
  title: string;
  onTitleChange: (v: string) => void;
  bible: SeriesBible;
  onBibleChange: (bible: SeriesBible) => void;
  /** How many episodes hang off this project, so the page says what the facts below govern. */
  episodeCount?: number;
  /** True while the project's own document is still loading. */
  loading?: boolean;
  /**
   * WHO HELPS SHAPE THE PROJECT (owner 2026-08-24: *"on Project also have a button like Story to pick the AI
   * agent helping with writing — this could also be a director or writer, given it's high-level"*). The bench
   * is deliberately WIDER than Story's: the plot, the world and the cast are a showrunner's decisions, and a
   * director thinks about them differently from a writer. Same seat, same hiring room as everywhere else.
   */
  leads?: SeatCandidate[];
  leadId?: string | null;
  onPickLead?: (agentId: string) => void | Promise<void>;
  /**
   * THE PLOT LIBRARY LIVES HERE NOW (owner 2026-08-27: the micro-drama level and the episode level must be
   * separated). Picking a plot shape is a decision about the SHOW — it was being asked once per episode, off
   * component state that was never even persisted, so a reload lost it and every episode started from
   * scratch. One pick, inherited by every episode.
   */
  templates?: StoryTemplate[];
  genre?: string | null;
  onPickGenre?: (key: string) => void;
  /** The project id, so an assist can write a field against what the project already established. */
  seriesId?: string | null;
  /**
   * Write the project NOW rather than on the autosave debounce. A reference plate is composed server-side
   * from this document, so a plate asked for a row the server has not seen yet fails on a row the creator can
   * see in front of them. Called immediately before generating one.
   */
  onFlush?: () => Promise<void>;
  /** Out of credits while writing a field — the caller raises the top-up. */
  onNeedsTopUp?: () => void;
  /** Open a planned row as a real episode (RF3-SHOWPLAN C) — planning mints nothing until this is used. */
  onOpenPlannedEpisode?: (n: number) => void;
}

/** How many of the required facts are stated — the number the stepper shows, derived in one place. */
export function projectFilled(bible: SeriesBible | undefined | null): number {
  if (!bible) return 0;
  return REQUIRED_BIBLE_FIELDS.filter((k) => {
    // `characters` became ROWS (owner 2026-08-28) and the legacy prose field still counts for a project
    // written before that — otherwise every existing project would silently drop to 3/4 on open.
    if (k === 'characters') return !!(bible.cast?.length || String(bible.characters || '').trim());
    return !!String(bible[k as ProseKey] || '').trim();
  }).length;
}

const ProjectStage: React.FC<ProjectStageProps> = ({
  title, onTitleChange, bible, onBibleChange, episodeCount, loading, leads, leadId, onPickLead, seriesId, onNeedsTopUp,
  templates, genre, onPickGenre, onOpenPlannedEpisode, onFlush,
}) => {
  const [draft, setDraft] = useState<SeriesBible>(bible);
  const [library, setLibrary] = useState(false);

  // The project loads after the page mounts, so adopt it when it lands — but never over the creator's typing.
  useEffect(() => { setDraft((d) => (Object.keys(d).length ? d : bible)); }, [bible]);

  const set = (key: ProseKey, value: string) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    onBibleChange(next);
  };

  /** The project's places. Edited as rows and written back as one field, like every other bible value. */
  const locations: SeriesLocation[] = draft.locations || [];
  const writeLocations = (next: SeriesLocation[]) => {
    const nextDraft = { ...draft, locations: next };
    setDraft(nextDraft);
    onBibleChange(nextDraft);
  };
  const addLocation = () => writeLocations([...locations, { id: `loc${Date.now().toString(36)}`, name: '' }]);
  const setLocation = (i: number, patch: Partial<SeriesLocation>) =>
    writeLocations(locations.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const removeLocation = (i: number) => writeLocations(locations.filter((_, j) => j !== i));

  const cast: SeriesCharacter[] = draft.cast || [];
  const writeCast = (next: SeriesCharacter[]) => {
    const nextDraft = { ...draft, cast: next };
    setDraft(nextDraft);
    onBibleChange(nextDraft);
  };

  const props: SeriesProp[] = draft.props || [];
  const writeProps = (next: SeriesProp[]) => {
    const nextDraft = { ...draft, props: next };
    setDraft(nextDraft);
    onBibleChange(nextDraft);
  };
  const addProp = () => writeProps([...props, { id: `prop${Date.now().toString(36)}`, name: '' }]);
  const setProp = (i: number, patch: Partial<SeriesProp>) =>
    writeProps(props.map((p2, j) => (j === i ? { ...p2, ...patch } : p2)));
  const removeProp = (i: number) => writeProps(props.filter((_, j) => j !== i));

  /**
   * ASK WHAT THE STORY NEEDS (RF3-PREPRO A). The writer reads the plot and proposes the characters, hero
   * objects and places it requires — and the result is MERGED by name, never applied raw. A creator who has
   * written two characters and presses this again must not lose them, which is the difference between a
   * proposal and a reset.
   */
  const [proposing, setProposing] = useState(false);
  const askWhatIsNeeded = async () => {
    if (proposing) return;
    setProposing(true);
    try {
      const r = await getStoryManifest(draft.premise || '', seriesId);
      if (r.needsTopUp) { onNeedsTopUp?.(); return; }
      const m = r.manifest;
      if (!m) return;

      const byName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
      const nextLocations = [...locations];
      for (const loc of m.locations) {
        if (nextLocations.some((l) => byName(l.name, loc.name))) continue;
        if (nextLocations.length >= MAX_LOCATIONS) break;
        nextLocations.push({ id: `loc${Date.now().toString(36)}${nextLocations.length}`, ...loc });
      }
      const nextProps = [...props];
      for (const prop of m.props) {
        if (nextProps.some((p2) => byName(p2.name, prop.name))) continue;
        if (nextProps.length >= MAX_LOCATIONS) break;
        nextProps.push({ id: `prop${Date.now().toString(36)}${nextProps.length}`, ...prop });
      }
      /* Characters are ROWS now, so a proposal seeds a row the creator then fills in depth — the note lands
         in `motivation`, which is the field a writer needs most and the one a one-liner actually answers. */
      const nextCast = [...cast];
      for (const c of m.characters) {
        if (nextCast.some((x) => byName(x.name, c.name))) continue;
        if (nextCast.length >= 10) break;
        nextCast.push({ id: `ch${Date.now().toString(36)}${nextCast.length}`, name: c.name, motivation: c.note });
      }

      const nextDraft = { ...draft, locations: nextLocations, props: nextProps, cast: nextCast };
      setDraft(nextDraft);
      onBibleChange(nextDraft);
    } finally {
      setProposing(false);
    }
  };

  const outstanding = useMemo(() => {
    const missing = FIELDS.filter((f) => REQUIRED.has(f.key) && !String(draft[f.key] || '').trim())
      .map((f) => f.label.toLowerCase());
    // The cast is a section rather than a field, so it reports itself.
    if (!draft.cast?.length && !String(draft.characters || '').trim()) missing.push('who is in it');
    return missing;
  }, [draft]);

  if (loading) {
    return (
      <div className="mx-auto flex w-full max-w-2xl items-center gap-2 py-8">
        <Loader2 className="h-4 w-4 animate-spin text-secondary-400" />
        <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>Opening your series…</p>
      </div>
    );
  }

  return (
    <div className="ms-page mx-auto w-full max-w-2xl py-5">
      <h2 className={`${TYPE.stageTitle} text-secondary-900 dark:text-white`}>Your series</h2>
      <p className={`mt-1 ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
        The plot, the world and the cast — stated once, here. Every episode is written against this, so your
        crew never has to guess what year it is or who these people are.
      </p>

      <label className="mt-4 block">
        <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Series name</span>
        <input
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          data-testid="project-title"
          placeholder="Hacker 2077"
          className={`mt-1 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 ${TYPE.manuscriptTitle}
                     text-secondary-900 outline-none transition-colors placeholder:text-secondary-400
                     focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900 dark:text-white
                     dark:placeholder:text-secondary-600 dark:focus:border-secondary-500`}
        />
        {!!episodeCount && (
          <span className={`mt-1 block ${TYPE.meta} text-secondary-400`}>
            {episodeCount} episode{episodeCount === 1 ? '' : 's'} inherit what is below.
          </span>
        )}
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!!templates?.length && onPickGenre && (
          <>
            <button
              type="button"
              data-testid="project-template"
              onClick={() => setLibrary(true)}
              className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-2.5 py-1
                         ${TYPE.control} text-secondary-600 transition-colors hover:border-secondary-400
                         dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-300
                         dark:hover:border-secondary-500`}
            >
              <Lightbulb className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
              {templates.find((t) => t.key === genre)?.label || 'No idea yet? Start from a plot'}
            </button>
            <TemplateModal
              open={library}
              onClose={() => setLibrary(false)}
              templates={templates}
              templateKey={genre || undefined}
              onPick={onPickGenre}
            />
          </>
        )}
      </div>

      {!!leads?.length && onPickLead && (
        <div className="mt-3">
          <AgentSeat
            compact
            testId="project-lead-seat"
            label="Your lead"
            seatTitle="Who shapes this series?"
            hiredLabel="Your lead"
            verb="Recruit"
            candidates={leads}
            currentId={leadId || null}
            onRecruit={(agentId) => onPickLead(agentId)}
          />
        </div>
      )}

      <div className="mt-4 space-y-4">
        {FIELDS.map((f) => {
          const value = String(draft[f.key] || '');
          const missing = REQUIRED.has(f.key) && !value.trim();
          return (
            <label key={f.key} className="block">
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>{f.label}</span>
                {/* Monochrome, and a WORD rather than an alarm colour — the point is to be told what is left,
                    not to be scolded for an unfinished draft. */}
                {missing && <span className={`${TYPE.meta} text-secondary-400`}>needed</span>}
                {!missing && REQUIRED.has(f.key) && <Check className="h-3 w-3 text-secondary-400" aria-hidden />}
                {/* The assist sits on the field's own LABEL ROW — the research's "toolbar, not inside the
                    box": a control floating in the input competes with the text it is meant to help write. */}
                {!f.noAssist && <AiAssist
                  field={f.key}
                  current={value}
                  seriesId={seriesId}
                  onWrite={(text) => set(f.key, text)}
                  onNeedsTopUp={onNeedsTopUp}
                />}
              </span>
              {/* GROWS WITH THE PLOT (owner 2026-08-24): a long premise pushes the box open rather than
                  hiding itself behind an inner scrollbar, so the creator can read what they wrote. */}
              <AutoTextarea
                value={value}
                onChange={(v) => set(f.key, v)}
                minRows={f.rows}
                placeholder={f.placeholder}
                data-testid={`project-${f.key}`}
                className={`mt-1 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2
                           ${f.key === 'look' ? `${TYPE.body} leading-relaxed` : TYPE.manuscript} text-secondary-900 outline-none transition-colors
                           placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                           dark:bg-secondary-900 dark:text-white dark:placeholder:text-secondary-600
                           dark:focus:border-secondary-500`}
              />
              <span className={`mt-1 block ${TYPE.meta} text-secondary-400`}>{f.hint}</span>
            </label>
          );
        })}
      </div>

      {/* WHAT THIS STORY NEEDS — one press, proposals merged into the sections below. */}
      {seriesId && (
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-secondary-200 pt-4 dark:border-secondary-600">
          <button
            type="button"
            onClick={() => void askWhatIsNeeded()}
            disabled={proposing || !String(draft.premise || '').trim()}
            data-testid="ask-manifest"
            className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg border border-secondary-200 px-3
                       ${TYPE.control} font-medium text-secondary-700 transition-colors hover:border-secondary-400
                       disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-200`}
          >
            {proposing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {proposing ? 'Reading your plot…' : 'What does this story need?'}
          </button>
          <span className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-400`}>
            Proposes the cast, objects and places your plot calls for. Adds to what you have — never replaces it.
          </span>
        </div>
      )}

      <ProjectCast
        cast={cast}
        onChange={writeCast}
        seriesId={seriesId}
        onFlush={onFlush}
        onNeedsTopUp={onNeedsTopUp}
      />

      {/*
        WHERE IT HAPPENS (RF3-PREPRO B2). A micro-drama runs on one to three places, and a place is a PROJECT
        fact: every episode set there must describe it identically or the render drifts. There was no such
        entity — only a per-scene token and a per-shot line, both re-typed per episode.

        The ANCHOR OBJECT is the field that earns this section. Production practice is to name one thing in the
        set that cannot change between shots ("a brass light fixture, a cracked tile pattern"): a model
        reproduces a named object far more reliably than a mood, so a location without one is atmosphere, and
        atmosphere drifts.
      */}
      <section className="mt-6" aria-label="Locations">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Where it happens</span>
          <span className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-400`}>
            One to three places is the format&apos;s own shape — more, and nothing gets established.
          </span>
          <button
            type="button"
            onClick={addLocation}
            disabled={locations.length >= MAX_LOCATIONS}
            data-testid="add-location"
            className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                       ${TYPE.control} text-secondary-600 transition-colors hover:border-secondary-400
                       disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
          >
            <Plus className="h-3.5 w-3.5" /> Add a place
          </button>
        </div>

        {locations.length === 0 ? (
          <p className={`mt-2 ${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
            No places yet. Every episode set somewhere you have named here reuses its exact wording, which is
            what keeps the same room looking like the same room.
          </p>
        ) : (
          <ul className="mt-2 space-y-2" data-testid="locations">
            {locations.map((loc, i) => (
              <li key={loc.id} className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-600">
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
                  <input
                    value={loc.name}
                    onChange={(e) => setLocation(i, { name: e.target.value })}
                    placeholder="The flooded market"
                    aria-label={`Location ${i + 1} name`}
                    data-testid={`location-name-${i}`}
                    className={`min-w-0 flex-1 rounded border-0 bg-transparent p-0 ${TYPE.sectionTitle}
                               text-secondary-900 outline-none placeholder:font-normal
                               placeholder:text-secondary-400 dark:text-white`}
                  />
                  <button
                    type="button"
                    onClick={() => removeLocation(i)}
                    aria-label={`Remove ${loc.name || `location ${i + 1}`}`}
                    className="shrink-0 rounded p-1 text-secondary-400 transition-colors hover:text-secondary-800 dark:hover:text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <span className="mt-2 flex flex-wrap items-baseline gap-x-2">
                  <span className={`${TYPE.meta} text-secondary-400`}>What it looks like</span>
                  <AiAssist
                    field="location"
                    current={loc.description || ''}
                    seriesId={seriesId}
                    label={(loc.description || '').trim() ? 'Improve' : 'Write it'}
                    onWrite={(text) => setLocation(i, { description: text })}
                    onNeedsTopUp={onNeedsTopUp}
                  />
                </span>
                <AutoTextarea
                  value={loc.description || ''}
                  onChange={(v) => setLocation(i, { description: v })}
                  minRows={2}
                  placeholder="What a camera would see, and how it feels to be there."
                  aria-label={`${loc.name || `Location ${i + 1}`} description`}
                  data-testid={`location-desc-${i}`}
                  className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2
                             ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                             placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                             dark:bg-secondary-900 dark:text-white`}
                />
                {/* SEE IT BEFORE A SHOT DOES (owner 2026-08-30). One cheap image, made in isolation, answers
                    "is this the place I meant?" — a question that otherwise first gets asked inside a finished
                    shot, where a wrong set is expensive to notice and hard to attribute. */}
                <ReferencePlate
                  path={loc.image_path}
                  onChange={(p) => setLocation(i, { image_path: p })}
                  seriesId={seriesId}
                  onFlush={onFlush}
                  kind="place"
                  itemId={loc.id}
                  described={!!String(loc.description || '').trim()}
                  folder={`studio/locations/${loc.id}`}
                  name={loc.name}
                  onNeedsTopUp={onNeedsTopUp}
                />
                <label className="mt-2 block">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className={`${TYPE.meta} text-secondary-400`}>
                      The one thing that never changes here
                    </span>
                    <AiAssist
                      field="anchor_object"
                      current={loc.anchor_object || ''}
                      seriesId={seriesId}
                      label={(loc.anchor_object || '').trim() ? 'Improve' : 'Write it'}
                      onWrite={(text) => setLocation(i, { anchor_object: text })}
                      onNeedsTopUp={onNeedsTopUp}
                    />
                  </span>
                  <input
                    value={loc.anchor_object || ''}
                    onChange={(e) => setLocation(i, { anchor_object: e.target.value })}
                    placeholder="A brass light fixture over the counter"
                    data-testid={`location-anchor-${i}`}
                    className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-1.5
                               ${TYPE.control} text-secondary-900 outline-none placeholder:text-secondary-400
                               focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900
                               dark:text-white`}
                  />
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ProjectEpisodes
        target={draft.episode_target || 12}
        onTargetChange={(n) => { const d = { ...draft, episode_target: n }; setDraft(d); onBibleChange(d); }}
        plan={draft.plan || []}
        onPlanChange={(plan) => { const d = { ...draft, plan }; setDraft(d); onBibleChange(d); }}
        plot={String(draft.premise || '')}
        seriesId={seriesId}
        onNeedsTopUp={onNeedsTopUp}
        onOpenEpisode={onOpenPlannedEpisode ? (row) => onOpenPlannedEpisode(row.n) : undefined}
      />

      {/*
        THE OBJECTS THE STORY TURNS ON (RF3-PREPRO A3). Only HERO props: the test is behavioural, not
        aesthetic — handled, recurring, or beat-driving. Say that in the caption, because asked for "objects"
        a creator (and a model) lists every noun in the script, and a locked reference for a coffee cup nobody
        touches is cost without benefit.
      */}
      <section className="mt-6" aria-label="Objects">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Objects that matter</span>
          <span className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-400`}>
            Only what someone handles, what keeps coming back, or what a scene turns on.
          </span>
          <button
            type="button"
            onClick={addProp}
            disabled={props.length >= MAX_LOCATIONS}
            data-testid="add-prop"
            className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                       ${TYPE.control} text-secondary-600 transition-colors hover:border-secondary-400
                       disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
          >
            <Plus className="h-3.5 w-3.5" /> Add an object
          </button>
        </div>

        {props.length === 0 ? (
          <p className={`mt-2 ${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
            Most stories need none. Add one only when an object has to look the same every time it appears.
          </p>
        ) : (
          <ul className="mt-2 space-y-2" data-testid="props">
            {props.map((prop, i) => (
              <li key={prop.id} className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-600">
                <div className="flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
                  <input
                    value={prop.name}
                    onChange={(e) => setProp(i, { name: e.target.value })}
                    placeholder="The courier deck"
                    aria-label={`Object ${i + 1} name`}
                    data-testid={`prop-name-${i}`}
                    className={`min-w-0 flex-1 rounded border-0 bg-transparent p-0 ${TYPE.sectionTitle}
                               text-secondary-900 outline-none placeholder:font-normal
                               placeholder:text-secondary-400 dark:text-white`}
                  />
                  <button
                    type="button"
                    onClick={() => removeProp(i)}
                    aria-label={`Remove ${prop.name || `object ${i + 1}`}`}
                    className="shrink-0 rounded p-1 text-secondary-400 transition-colors hover:text-secondary-800 dark:hover:text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <span className="mt-2 flex flex-wrap items-baseline gap-x-2">
                  <span className={`${TYPE.meta} text-secondary-400`}>What it looks like</span>
                  <AiAssist
                    field="object"
                    current={prop.description || ''}
                    seriesId={seriesId}
                    label={(prop.description || '').trim() ? 'Improve' : 'Write it'}
                    onWrite={(text) => setProp(i, { description: text })}
                    onNeedsTopUp={onNeedsTopUp}
                  />
                </span>
                <AutoTextarea
                  value={prop.description || ''}
                  onChange={(v) => setProp(i, { description: v })}
                  minRows={2}
                  placeholder="What it looks like, worded once and reused every time."
                  aria-label={`${prop.name || `Object ${i + 1}`} description`}
                  data-testid={`prop-desc-${i}`}
                  className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2
                             ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                             placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                             dark:bg-secondary-900 dark:text-white`}
                />
                {/* A hero object earns a plate for the same reason a place does — this wording gets pasted
                    into every shot the object appears in, so being wrong about it is expensive and repeated.
                    Shot on plain ground, because a prop photographed in a scene drags the scene along with it. */}
                <ReferencePlate
                  path={prop.image_path}
                  onChange={(p) => setProp(i, { image_path: p })}
                  seriesId={seriesId}
                  onFlush={onFlush}
                  kind="object"
                  itemId={prop.id}
                  described={!!String(prop.description || '').trim()}
                  folder={`studio/objects/${prop.id}`}
                  name={prop.name}
                  onNeedsTopUp={onNeedsTopUp}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className={`mt-5 ${TYPE.control} text-secondary-500 dark:text-secondary-400`} data-testid="project-outstanding">
        {outstanding.length
          ? `Still to state: ${outstanding.join(', ')}. You can write an episode without them, but your crew will be inventing the answers.`
          : 'Your series is set up. Every episode is written against this.'}
      </p>
    </div>
  );
};

export default ProjectStage;
